import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "../lib/auth";
import { api } from "../lib/api";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/pro-solid-svg-icons/faBuilding";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";

export const Route = createFileRoute("/create-org")({
  component: CreateOrgPage,
});

// Org types matching DB enum - used for dropdown
const ORG_TYPES = [
  { value: "zoo", label: "Zoo" },
  { value: "garden", label: "Garden" },
  { value: "fair", label: "Fair" },
  { value: "park", label: "Park" },
  { value: "default", label: "Other" },
] as const;

/**
 * Converts a string to a URL-friendly slug
 * - Lowercase
 * - Replace spaces/underscores with hyphens
 * - Remove non-alphanumeric characters (except hyphens)
 * - Collapse multiple hyphens
 */
function toSlug(str: string): string {
  return str
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function CreateOrgPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session, isPending: isSessionLoading } = useSession();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [type, setType] = useState<string>("default");
  const [error, setError] = useState<string | null>(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isSessionLoading && !session?.user) {
      navigate({
        to: "/login",
        search: { redirect: "/create-org" },
      });
    }
  }, [isSessionLoading, session, navigate]);

  // Auto-generate slug from name (unless manually edited)
  useEffect(() => {
    if (!slugEdited && name) {
      setSlug(toSlug(name));
    }
  }, [name, slugEdited]);

  const createOrgMutation = useMutation({
    mutationFn: async () => {
      const res = await api.orgs.$post({
        json: {
          name: name.trim(),
          slug: slug.trim(),
          type: type as "zoo" | "garden" | "fair" | "park" | "default",
        },
      });

      if (!res.ok) {
        const data = await res.json();
        if (res.status === 409) {
          throw new Error("Organization slug already exists");
        }
        throw new Error((data as { error?: string }).error || "Failed to create organization");
      }

      return res.json();
    },
    onSuccess: async (data) => {
      // Invalidate orgs cache so OrgContext sees new membership
      await queryClient.invalidateQueries({ queryKey: ["orgs", "me"] });
      // Redirect to ingest wizard in new org context
      navigate({ to: `/${data.organization.slug}/admin/ingest` });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to create organization");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    if (!name.trim()) {
      setError("Organization name is required");
      return;
    }
    if (!slug.trim()) {
      setError("Organization slug is required");
      return;
    }
    if (!/^[a-z0-9-]+$/.test(slug)) {
      setError("Slug must be lowercase letters, numbers, and hyphens only");
      return;
    }

    createOrgMutation.mutate();
  };

  // Loading state
  if (isSessionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-2 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!session?.user) {
    return null;
  }

  const isLoading = createOrgMutation.isPending;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <FontAwesomeIcon icon={faBuilding} className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Create Organization</CardTitle>
          <CardDescription>
            Set up your organization to start creating maps
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Organization Name */}
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name *</Label>
              <Input
                id="org-name"
                placeholder="e.g., San Diego Zoo"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label htmlFor="org-slug">URL Slug *</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">/</span>
                <Input
                  id="org-slug"
                  placeholder="san-diego-zoo"
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    setSlugEdited(true);
                  }}
                  disabled={isLoading}
                  className="font-mono"
                />
                <span className="text-sm text-muted-foreground">/admin</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Lowercase letters, numbers, and hyphens only
              </p>
            </div>

            {/* Venue Type */}
            <div className="space-y-2">
              <Label htmlFor="org-type">Venue Type *</Label>
              <Select value={type} onValueChange={setType} disabled={isLoading}>
                <SelectTrigger id="org-type">
                  <SelectValue placeholder="Select venue type" />
                </SelectTrigger>
                <SelectContent>
                  {ORG_TYPES.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Used for default categories and OSM query presets
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            {/* Submit */}
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4 mr-2 " />
                  Creating...
                </>
              ) : (
                "Create Organization"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
